<script setup lang="ts">
import type { UIStressTest } from '@/composables/rc/miner-stress-tests'
import { DiagnosticResult } from '@/types/rc/miners'
import { Icon } from '@iconify/vue'
import { DateTime } from 'luxon'
import { computed } from 'vue'
import AttachmentsList from '../repairs-sidebar/miner-sidebar-sections/AttachmentsList.vue'

const props = defineProps<{ stressTest: UIStressTest }>()
const emit = defineEmits(['flagDelete'])

const flagged = computed(() => props.stressTest.flaggedDelete)
const failed = computed(() => props.stressTest.result == DiagnosticResult.FAIL)
const passed = computed(() => props.stressTest.result == DiagnosticResult.PASS)

const date = computed(() => DateTime.fromISO(props.stressTest.date!))
const day = computed(() => date.value.toLocaleString(DateTime.DATE_MED))
const time = computed(() => date.value.toLocaleString(DateTime.TIME_24_WITH_SECONDS))

const deleteStressTest = () => {
  emit('flagDelete', props.stressTest.id)
}
</script>

<template>
  <div :class="['stress-test-card', { flagged, failed, passed }]">
    <div class="stress-test-card__header">
      <div class="stress-test-card__header__date">
        <Icon v-if="stressTest.isLatest" class="stress-test-card__header__dot" icon="fluent:circle-16-filled" />
        {{ day }} {{ time }}
        <span v-if="stressTest.isLatest">(Latest)</span>
      </div>
      <div class="stress-test-card__header__trash" @click="deleteStressTest"><Icon icon="mdi:trash-can-outline" /></div>
    </div>
    <div class="stress-test-card__body">
      <AttachmentsList v-if="stressTest.files.length > 0" :files="stressTest.files" class="w-[200px]" />

      <div class="stress-test-card__body__text">
        <p class="stress-test-card__body__text__result">{{ failed ? 'Failed' : 'Passed' }}</p>
        <p>{{ stressTest.failure_reasons }}</p>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.stress-test-card {
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &__dot {
      color: var(--fill-brand);
    }

    &__date {
      display: flex;
      align-items: center;
      gap: var(--spacing-spacing-sm);
    }

    &__trash {
      cursor: pointer;
      visibility: hidden;
    }
  }

  &.flagged {
    .stress-test-card__header__trash {
      color: var(--component-stroke-error);
      visibility: visible;
    }
  }

  &.failed {
    .stress-test-card__body__text__result {
      color: var(--component-stroke-error);
    }
  }

  &.passed {
    .stress-test-card__body__text__result {
      color: var(--component-stroke-emerald);
    }
  }

  &:hover {
    .stress-test-card__header__trash {
      visibility: visible;
    }
  }

  &__body {
    display: flex;
    gap: var(--spacing-spacing-lg);
    margin-top: var(--spacing-spacing-lg);

    &__text {
      color: var(--text-primary);
      display: flex;
      flex-direction: column;
      gap: var(--spacing-spacing-sm);
      max-width: 50%;
    }
  }
}
</style>
